// src/core/consent-manager.ts

import type { ConsentState, ConsentRecord, CategoryDefinition } from '../types'
import { EVENTS } from '../constants/defaults'
import {
  saveConsent,
  loadConsent,
  clearConsent,
  hasConsent as storageHasConsent,
  isConsentExpired,
  exportConsentData,
} from './storage'
import { applyConsent, observeNewElements } from './script-blocker'
import { updateConsentMode } from '../integrations/consent-mode'
import { debugLog } from '../utils/helpers'

// Global state
let currentConsent: ConsentState = {}
const consentCallbacks: Set<(consent: ConsentState) => void> = new Set()
let mutationObserver: MutationObserver | null = null

/**
 * Initialize consent manager
 */
export function initConsentManager(
  categories: CategoryDefinition[],
  version: string,
  ttl: number
): ConsentState | null {
  debugLog('Initializing consent manager', { version, ttl })

  // Load existing consent
  const record = loadConsent()

  // Check if consent exists and is valid
  if (record) {
    // Check version
    if (record.version !== version) {
      debugLog('Version mismatch - clearing consent', {
        stored: record.version,
        current: version,
      })
      clearConsent()
      return null
    }

    // Check expiry
    if (isConsentExpired(record, ttl)) {
      debugLog('Consent expired - clearing')
      clearConsent()
      return null
    }

    // Valid consent found - update consent mode
    currentConsent = record.choices
    debugLog('Valid consent found', currentConsent)

    // CRITICAL: Update Google Consent Mode with existing consent
    updateConsentMode(currentConsent)

    return currentConsent
  }

  // No valid consent
  debugLog('No valid consent found')
  return null
}

/**
 * Set consent choices
 */
export function setConsent(
  consent: ConsentState,
  version: string,
  language: string,
  ttl: number,
  domain?: string,
  onBlock?: (src: string) => void
): void {
  debugLog('Setting consent', consent)

  currentConsent = consent

  // Save to storage
  saveConsent(consent, version, language, ttl, domain)

  // CRITICAL: Update Google Consent Mode
  updateConsentMode(consent)

  // Apply to blocked scripts/iframes
  applyConsent(consent, onBlock)

  // Start observing for dynamically added elements
  if (!mutationObserver) {
    mutationObserver = observeNewElements(consent, onBlock)
  }

  // Notify callbacks
  notifyConsentChange(consent)

  // Dispatch event
  dispatchConsentEvent(EVENTS.CONSENT_GIVEN, consent)
}

/**
 * Get current consent state
 */
export function getConsent(category?: string): boolean {
  if (!category) {
    return Object.keys(currentConsent).length > 0
  }

  return currentConsent[category] === true
}

/**
 * Get all consent choices
 */
export function getAllConsent(): ConsentState {
  return { ...currentConsent }
}

/**
 * Revoke all consent
 */
export function revokeConsent(domain?: string): void {
  debugLog('Revoking consent')

  currentConsent = {}
  clearConsent(domain)

  // CRITICAL: Update consent mode to denied
  updateConsentMode({})

  // Stop observing
  if (mutationObserver) {
    mutationObserver.disconnect()
    mutationObserver = null
  }

  // Notify callbacks
  notifyConsentChange({})

  // Dispatch event
  dispatchConsentEvent(EVENTS.CONSENT_REVOKED, {})
}

/**
 * Check if user has given consent
 */
export function hasConsent(): boolean {
  return storageHasConsent()
}

/**
 * Subscribe to consent changes
 */
export function onConsentChange(callback: (consent: ConsentState) => void): () => void {
  consentCallbacks.add(callback)

  // Return unsubscribe function
  return () => {
    consentCallbacks.delete(callback)
  }
}

/**
 * Notify all callbacks of consent change
 */
function notifyConsentChange(consent: ConsentState): void {
  consentCallbacks.forEach(callback => {
    try {
      callback(consent)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[MEUI Cookies] Error in consent callback:', error)
    }
  })
}

/**
 * Dispatch custom event
 */
function dispatchConsentEvent(eventName: string, consent: ConsentState): void {
  if (typeof window === 'undefined') return

  const event = new CustomEvent(eventName, {
    detail: { consent },
  })

  window.dispatchEvent(event)
  debugLog('Event dispatched', eventName)
}

/**
 * Export consent data for GDPR request
 */
export function exportConsent(): ConsentRecord | null {
  return exportConsentData()
}

/**
 * Accept all categories
 */
export function acceptAll(
  categories: CategoryDefinition[],
  version: string,
  language: string,
  ttl: number,
  domain?: string,
  onBlock?: (src: string) => void
): void {
  const consent: ConsentState = {}

  categories.forEach(category => {
    consent[category.id] = true
  })

  setConsent(consent, version, language, ttl, domain, onBlock)
}

/**
 * Decline all non-required categories
 */
export function declineAll(
  categories: CategoryDefinition[],
  version: string,
  language: string,
  ttl: number,
  domain?: string,
  onBlock?: (src: string) => void
): void {
  const consent: ConsentState = {}

  categories.forEach(category => {
    // Only accept required categories
    consent[category.id] = category.required === true
  })

  setConsent(consent, version, language, ttl, domain, onBlock)
}

/**
 * Clean up
 */
export function cleanup(): void {
  if (mutationObserver) {
    mutationObserver.disconnect()
    mutationObserver = null
  }

  consentCallbacks.clear()
  currentConsent = {}

  debugLog('Consent manager cleaned up')
}
